/**
 * CoachAffiliationsService - Manage coach-club relationships
 * Feature: Coach independence Phase 2
 *
 * NOTE: All queries avoid PostgREST implicit joins (*, clubs(*)) because the
 * coach_club_affiliations → clubs FK may not yet be in the PostgREST schema
 * cache. Club data is fetched with a separate query and merged manually.
 */

import { BaseService, ServiceResponse } from '@/services/api/base.service';
import { Database } from '@/integrations/supabase/types';

type ClubRow = Database['public']['Tables']['clubs']['Row'];

// ── Club-perspective types ────────────────────────────────────────────────────

export interface ClubCoachAffiliation {
  id: string;
  coachId: string;
  clubId: string;
  status: 'pending' | 'active' | 'suspended' | 'left';
  invitedByClub: boolean;
  acceptedAt: string | null;
  createdAt: string;
  coach?: {
    id: string;
    userId: string;
    displayName: string | null;
    avatarUrl: string | null;
    bio: string | null;
    specialties: string[] | null;
    certifications: string[] | null;
    yearsExperience: number | null;
    hourlyRate: number | null;
    acceptsNewStudents: boolean | null;
  };
}

export interface CoachSearchResult {
  coachProfileId: string;
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  email: string | null;
  bio: string | null;
  specialties: string[] | null;
  certifications: string[] | null;
  yearsExperience: number | null;
  hourlyRate: number | null;
  acceptsNewStudents: boolean | null;
  existingAffiliationStatus?: 'pending' | 'active' | 'suspended' | 'left';
}

// ── Coach-perspective types ───────────────────────────────────────────────────

export interface CoachClubAffiliation {
  id: string;
  coachId: string;
  clubId: string;
  status: 'pending' | 'active' | 'suspended' | 'left';
  invitedByClub: boolean;
  acceptedAt: string | null;
  leftAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  club?: ClubRow;
}

export interface CreateAffiliationInput {
  coachId: string;
  clubId: string;
  invitedByClub?: boolean;
}

export interface UpdateAffiliationInput {
  status?: 'pending' | 'active' | 'suspended' | 'left';
  acceptedAt?: string | null;
  leftAt?: string | null;
  notes?: string;
}

class CoachAffiliationsServiceClass extends BaseService {

  private mapAffiliation(aff: any, club?: ClubRow): CoachClubAffiliation {
    return {
      id: aff.id,
      coachId: aff.coach_id,
      clubId: aff.club_id,
      status: aff.status as 'pending' | 'active' | 'suspended' | 'left',
      invitedByClub: aff.invited_by_club,
      acceptedAt: aff.accepted_at,
      leftAt: aff.left_at,
      notes: aff.notes,
      createdAt: aff.created_at,
      updatedAt: aff.updated_at,
      club,
    };
  }

  private async fetchClub(clubId: string): Promise<ClubRow | undefined> {
    const { data } = await this.supabase
      .from('clubs')
      .select('*')
      .eq('id', clubId)
      .single();
    return data as ClubRow | undefined;
  }

  /**
   * Get all affiliations for current coach
   */
  async getCoachAffiliations(): Promise<ServiceResponse<CoachClubAffiliation[]>> {
    return this.withErrorHandling(async () => {
      const { data: { user }, error: authError } = await this.supabase.auth.getUser();
      if (authError || !user) throw new Error('User not authenticated');

      const { data: coachProfiles, error: coachError } = await this.supabase
        .from('coach_profiles')
        .select('id')
        .eq('user_id', user.id);

      if (coachError) throw coachError;
      if (!coachProfiles || coachProfiles.length === 0) return [];

      const coachIds = coachProfiles.map(cp => cp.id);

      const { data, error } = await this.supabase
        .from('coach_club_affiliations')
        .select('*')
        .in('coach_id', coachIds)
        .order('created_at', { ascending: false });
      if (error) throw error;
      if (!data || data.length === 0) return [];

      const clubIds = [...new Set(data.map(aff => aff.club_id))];
      const { data: clubs } = await this.supabase
        .from('clubs')
        .select('*')
        .in('id', clubIds);

      const clubMap = new Map((clubs || []).map(c => [c.id, c as ClubRow]));

      return data.map(aff => this.mapAffiliation(aff, clubMap.get(aff.club_id)));
    });
  }

  /**
   * Get active club affiliations for coach (only 'active' status)
   */
  async getActiveClubAffiliations(): Promise<ServiceResponse<CoachClubAffiliation[]>> {
    const response = await this.getCoachAffiliations();
    if (!response.success || !response.data) return response;
    return {
      ...response,
      data: response.data.filter(aff => aff.status === 'active'),
    };
  }

  /**
   * Request to join a club (coach initiates).
   * Uses upsert so a coach who previously left can re-request.
   */
  async requestToJoinClub(clubId: string, coachId: string): Promise<ServiceResponse<CoachClubAffiliation>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coach_club_affiliations')
        .upsert(
          {
            coach_id: coachId,
            club_id: clubId,
            status: 'pending',
            invited_by_club: false,
            left_at: null,
            accepted_at: null,
          },
          { onConflict: 'coach_id,club_id' }
        )
        .select('*')
        .single();

      if (error) throw error;
      return this.mapAffiliation(data, await this.fetchClub(clubId));
    });
  }

  /**
   * Accept a club affiliation invitation (coach accepts invite from club)
   */
  async acceptAffiliation(affiliationId: string): Promise<ServiceResponse<CoachClubAffiliation>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coach_club_affiliations')
        .update({
          status: 'active',
          accepted_at: new Date().toISOString(),
        })
        .eq('id', affiliationId)
        .select('*')
        .single();

      if (error) throw error;
      return this.mapAffiliation(data, await this.fetchClub(data.club_id));
    });
  }

  /**
   * Decline or leave a club affiliation
   */
  async leaveClub(affiliationId: string): Promise<ServiceResponse<CoachClubAffiliation>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coach_club_affiliations')
        .update({
          status: 'left',
          left_at: new Date().toISOString(),
        })
        .eq('id', affiliationId)
        .select('*')
        .single();

      if (error) throw error;
      return this.mapAffiliation(data, await this.fetchClub(data.club_id));
    });
  }

  /**
   * Get affiliation details including club info
   */
  async getAffiliation(affiliationId: string): Promise<ServiceResponse<CoachClubAffiliation>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coach_club_affiliations')
        .select('*')
        .eq('id', affiliationId)
        .single();

      if (error) throw error;
      return this.mapAffiliation(data, await this.fetchClub(data.club_id));
    });
  }

  // ── Club-perspective methods ────────────────────────────────────────────────

  /**
   * Get all active + pending affiliations for a club (admin view)
   */
  async getClubAffiliations(clubId: string): Promise<ServiceResponse<ClubCoachAffiliation[]>> {
    return this.withErrorHandling(async () => {
      const { data: affRows, error: affError } = await this.supabase
        .from('coach_club_affiliations')
        .select('*')
        .eq('club_id', clubId)
        .in('status', ['pending', 'active'])
        .order('created_at', { ascending: false });

      if (affError) throw affError;
      if (!affRows || affRows.length === 0) return [];

      const coachIds = [...new Set(affRows.map(r => r.coach_id))];

      const { data: coachProfiles } = await this.supabase
        .from('coach_profiles')
        .select('id, user_id, bio, specialties, certifications, years_experience, hourly_rate, accepts_new_students')
        .in('id', coachIds);

      const userIds = [...new Set((coachProfiles || []).map(cp => cp.user_id))];

      const { data: profiles } = await this.supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', userIds);

      const profileMap = new Map((profiles || []).map(p => [p.id, p]));
      const coachMap = new Map((coachProfiles || []).map(cp => [cp.id, cp]));

      return affRows.map(row => {
        const cp = coachMap.get(row.coach_id);
        const profile = cp ? profileMap.get(cp.user_id) : undefined;
        return {
          id: row.id,
          coachId: row.coach_id,
          clubId: row.club_id,
          status: row.status as ClubCoachAffiliation['status'],
          invitedByClub: row.invited_by_club,
          acceptedAt: row.accepted_at,
          createdAt: row.created_at,
          coach: cp ? {
            id: cp.id,
            userId: cp.user_id,
            displayName: profile?.display_name ?? null,
            avatarUrl: profile?.avatar_url ?? null,
            bio: cp.bio,
            specialties: cp.specialties,
            certifications: cp.certifications ?? null,
            yearsExperience: cp.years_experience,
            hourlyRate: cp.hourly_rate,
            acceptsNewStudents: cp.accepts_new_students,
          } : undefined,
        };
      });
    });
  }

  /**
   * Club approves a pending join request from a coach
   */
  async approveCoachRequest(affiliationId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { error } = await this.supabase
        .from('coach_club_affiliations')
        .update({ status: 'active', accepted_at: new Date().toISOString() })
        .eq('id', affiliationId);
      if (error) throw error;
    });
  }

  /**
   * Club removes / rejects a coach affiliation
   */
  async removeCoach(affiliationId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { error } = await this.supabase
        .from('coach_club_affiliations')
        .update({ status: 'left', left_at: new Date().toISOString() })
        .eq('id', affiliationId);
      if (error) throw error;
    });
  }

  /**
   * Club invites a coach by coach_profile id (upsert handles re-invites)
   */
  async inviteCoach(clubId: string, coachProfileId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { error } = await this.supabase
        .from('coach_club_affiliations')
        .upsert(
          {
            coach_id: coachProfileId,
            club_id: clubId,
            status: 'pending',
            invited_by_club: true,
            left_at: null,
            accepted_at: null,
          },
          { onConflict: 'coach_id,club_id' }
        );
      if (error) throw error;
    });
  }

  /**
   * Search coaches by display_name or email, excluding those already affiliated with a club
   */
  async searchCoaches(query: string, excludeClubId?: string): Promise<ServiceResponse<CoachSearchResult[]>> {
    return this.withErrorHandling(async () => {
      const trimmed = query.trim();

      // Start from coach_profiles (admin RLS allows this), then join profiles for names
      const { data: coachProfiles, error: coachError } = await this.supabase
        .from('coach_profiles')
        .select('id, user_id, bio, specialties, certifications, years_experience, hourly_rate, accepts_new_students')
        .limit(50);

      if (coachError) throw coachError;
      if (!coachProfiles || coachProfiles.length === 0) return [];

      const userIds = coachProfiles.map(cp => cp.user_id);

      const { data: allProfiles, error: profileError } = await this.supabase
        .from('profiles')
        .select('id, display_name, avatar_url, email')
        .in('id', userIds);

      if (profileError) throw profileError;

      const profileMap = new Map((allProfiles || []).map(p => [p.id, p]));

      // Filter by query against display_name or email if provided
      let matchingCoaches = coachProfiles;
      if (trimmed) {
        const q = trimmed.toLowerCase();
        matchingCoaches = coachProfiles.filter(cp => {
          const p = profileMap.get(cp.user_id);
          return (
            p?.display_name?.toLowerCase().includes(q) ||
            p?.email?.toLowerCase().includes(q)
          );
        });
      }

      if (matchingCoaches.length === 0) return [];

      // Fetch existing affiliations with this club (if provided)
      let existingMap = new Map<string, string>();
      if (excludeClubId) {
        const coachIds = matchingCoaches.map(cp => cp.id);
        const { data: existing } = await this.supabase
          .from('coach_club_affiliations')
          .select('coach_id, status')
          .eq('club_id', excludeClubId)
          .in('coach_id', coachIds);
        existingMap = new Map((existing || []).map(e => [e.coach_id, e.status]));
      }

      return matchingCoaches.map(cp => {
        const profile = profileMap.get(cp.user_id);
        return {
          coachProfileId: cp.id,
          userId: cp.user_id,
          displayName: profile?.display_name ?? null,
          avatarUrl: profile?.avatar_url ?? null,
          email: profile?.email ?? null,
          bio: cp.bio,
          specialties: cp.specialties,
          certifications: cp.certifications ?? null,
          yearsExperience: cp.years_experience,
          hourlyRate: cp.hourly_rate,
          acceptsNewStudents: cp.accepts_new_students,
          existingAffiliationStatus: existingMap.get(cp.id) as CoachSearchResult['existingAffiliationStatus'],
        };
      });
    });
  }

}

export const coachAffiliationsService = new CoachAffiliationsServiceClass();
